import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './themes/themes.css';

// Import pages
import SelectFundsPage from './pages/SelectFundsPage';
import OptimizePage from './pages/OptimizePage';
import ResultsPageWrapper from './pages/ResultsPageWrapper';
import BacktestPageWrapper from './pages/BacktestPageWrapper';

// Import components
import AuthPage from './components/AuthPage';
import Stepper from './components/Stepper';
import Onboarding, { useOnboarding } from './components/Onboarding';
import ErrorBoundary from './components/ErrorBoundary';
import UniverseError from './components/UniverseError';
import TierBadge from './components/TierBadge';
import PricingPage from './components/PricingPage';
import ChatWidget from './components/ChatWidget';

// Import context
import { AuthProvider, useAuth } from './context/AuthContext';
import { PortfolioProvider, usePortfolio } from './context/PortfolioContext';
import { ErrorProvider } from './context/ErrorContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Layout component with header and stepper
const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { startOver, rateLimitError, clearRateLimitError } = usePortfolio();
  const { showPricing, setShowPricing, currentTier, changeTier } = useTheme();

  // Show rate limit error overlay
  if (rateLimitError) {
    return (
      <UniverseError
        message={rateLimitError.message}
        showDots={false}
        showEquation={false}
        onDismiss={clearRateLimitError}
        buttonText="I understand"
        onRetry={null}
      />
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-brand" onClick={startOver} style={{ cursor: 'pointer' }} title="Start Over">
            <img
              src="/allocq-icon.png"
              alt="AllocQ"
              className="header-logo-icon"
            />
            <div className="header-text">
              <img
                src="/allocq-wordmark.png"
                alt="AllocQ"
                className="header-wordmark"
              />
              <img
                src="/allocq-subtitle.png"
                alt="AI-Powered Portfolio Allocation Engine"
                className="header-subtitle"
              />
            </div>
          </div>
          {/* Tier Badge */}
          <TierBadge />
          {/* User menu */}
          <div className="user-menu">
            <span className="user-name">{user?.name || user?.email}</span>
            <button className="logout-btn" onClick={logout} title="Logout">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Pricing Page Modal */}
      {showPricing && (
        <PricingPage
          currentTier={currentTier}
          onSelectTier={changeTier}
          onClose={() => setShowPricing(false)}
        />
      )}

      <Stepper />

      <main className="App-main">
        {children}
      </main>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

// Main app content wrapped for auth
function AppContent() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { showOnboarding, isChecking, completeOnboarding } = useOnboarding(user?.email);

  // Show loading state while checking auth
  if (authLoading || isChecking) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <div className="loading-spinner-large"></div>
          <p style={{ marginTop: '16px', color: '#94A3B8' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth page if not authenticated
  if (!isAuthenticated) {
    return <AuthPage onAuthSuccess={() => window.location.reload()} />;
  }

  return (
    <PortfolioProvider>
      <AppLayout>
        <Routes>
          <Route path="/select" element={<SelectFundsPage />} />
          <Route path="/optimize" element={<OptimizePage />} />
          <Route path="/results" element={<ResultsPageWrapper />} />
          <Route path="/backtest" element={<BacktestPageWrapper />} />
          <Route path="/" element={<Navigate to="/select" replace />} />
          <Route path="*" element={<Navigate to="/select" replace />} />
        </Routes>
      </AppLayout>
      {/* Chat Widget - available on all pages */}
      <ChatWidget />
      {/* Onboarding overlay for first-time users */}
      {showOnboarding && <Onboarding onComplete={completeOnboarding} userEmail={user?.email} />}
    </PortfolioProvider>
  );
}

// Main App component with AuthProvider wrapper
function App() {
  return (
    <ErrorBoundary>
      <ErrorProvider>
        <ThemeProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ThemeProvider>
      </ErrorProvider>
    </ErrorBoundary>
  );
}

export default App;
